import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, MailCheck, ArrowLeft } from "lucide-react";
import { api, apiErr } from "./lib/api";
import { Btn, Input, Label, Card } from "./ui";

function AuthFrame({ title, subtitle, children }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-[#0B0F17] via-[#0F172A] to-[#111827]">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center text-center mb-6">
          <img src="/iws-logo.png" alt="IWS" className="h-16 w-auto mb-3" />
          <div className="font-heading font-extrabold text-xl text-slate-100 leading-none">IWS Secure Billing</div>
          <div className="text-xs font-mono text-slate-500 mt-1">Infomedia Web Solutions</div>
        </div>
        <Card className="p-8">
          <h1 className="text-2xl font-bold font-heading text-slate-100 mb-1">{title}</h1>
          <p className="text-sm text-slate-400 mb-6">{subtitle}</p>
          {children}
        </Card>
      </div>
    </div>
  );
}

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/auth/forgot-password", { email }); setSent(true); }
    catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };
  return (
    <AuthFrame title="Forgot password" subtitle="We'll email you a one-time link valid for 1 hour.">
      {sent ? (
        <div data-testid="forgot-sent" className="text-center space-y-3">
          <MailCheck className="mx-auto text-emerald-400" size={36} />
          <p className="text-slate-300 text-sm">If an account exists for <b className="text-slate-100">{email}</b>, a reset link is on its way. Check spam if it doesn't arrive within a minute.</p>
          <Link to="/" className="inline-flex items-center gap-1 text-sky-400 text-sm hover:underline" data-testid="forgot-back-login"><ArrowLeft size={14} /> Back to sign in</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div><Label>Email</Label><Input data-testid="forgot-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" /></div>
          <Btn data-testid="forgot-submit" type="submit" disabled={busy} className="w-full">{busy ? "Sending…" : "Email me a reset link"}</Btn>
          <Link to="/" className="block text-center text-sm text-slate-400 hover:text-slate-200" data-testid="forgot-cancel">Back to sign in</Link>
        </form>
      )}
    </AuthFrame>
  );
}

export function ResetPassword() {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return toast.error("Passwords do not match");
    setBusy(true);
    try { await api.post("/auth/reset-password", { token, new_password: pw }); toast.success("Password updated — please sign in"); nav("/"); }
    catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };
  if (!token) return <AuthFrame title="Invalid link" subtitle="This reset link is missing its token."><Link to="/forgot-password" className="text-sky-400 text-sm hover:underline">Request a new link</Link></AuthFrame>;
  return (
    <AuthFrame title="Set a new password" subtitle="10+ characters with upper, lower, number and symbol.">
      <form onSubmit={submit} className="space-y-4">
        <div><Label>New password</Label><Input data-testid="reset-new" type="password" required value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password" /></div>
        <div><Label>Confirm new password</Label><Input data-testid="reset-confirm" type="password" required value={pw2} onChange={(e) => setPw2(e.target.value)} autoComplete="new-password" /></div>
        <Btn data-testid="reset-submit" type="submit" disabled={busy} className="w-full"><KeyRound size={16} /> {busy ? "Saving…" : "Update password"}</Btn>
      </form>
    </AuthFrame>
  );
}
